import { useCallback, useMemo } from 'react'
import { ComponentWithPermissions } from '../../../../../../../baseComponents/CreateRoutering/types'
import useConfigColumns from '../../../../../../../utils/hooks/useConfigColumns'
import { InputTableHookProps, ColumnITLayoutTableConfiguration, InputTableFooterFullProps, ITHookFunctionTy } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable/types'
import TableWithCustomVirtualListing from '../../../../../../../baseComponents/TwinTable/TableWithCustomVirtualListing'
import { TableConfiguration, HeaderChangesTy } from '../../../../../../../baseComponents/TwinTable/types'
import { addDictionaryExtraLine, TwinDictionary } from '../../../../../../../utils/globals/dictionary'
import useTwinTranslation from '../../../../../../../utils/hooks/useTwinTranslation'
import { InputCalendarStateFull } from '../../../../../../../forms/Input/InputCalendar'
import { CustomSelectWithSearchBar } from '../../../../../../../forms/CustomSelect'
import { PaymentMethodsPayload } from '../../../../../../../utils/reducers/payments/paymentMethods'
import { PaymentTypesPayload } from '../../../../../../../utils/reducers/payments/paymentTypes'
import { InputTableLayoutData } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/types'
import { RemittanceDataFilters } from '../Remittances/ModalNewRemittance/types'
import { filterRemittanceData } from '../Remittances/ModalNewRemittance/ModalSelectNewRemittance/functions'
import { SearchBarInputTable } from '../../../../../../../baseComponents/TwinTable/InputTableLayout/InputTable'
import { ButtonConfigColumnsWithModal } from '../../../../../../../baseComponents/ModalsLayouts/ModalConfigColumnsTabs'

type InputTableSelectPaymentMethodCopyDatesProps = ComponentWithPermissions & InputTableHookProps & {
    tableConfiguration: TableConfiguration
    columns: ColumnITLayoutTableConfiguration[]
    className?: string
    name: string
    tableTitle?: string
    TableChildren: React.FC<InputTableFooterFullProps>
    hookFunction: ITHookFunctionTy
    headerChanges?: HeaderChangesTy
    orderStatusDict: TwinDictionary
    paymentTypes: PaymentTypesPayload
    paymentMethods: PaymentMethodsPayload
    extraFields?: {
        Component: React.FC<any>
        field: string
        label: string
        extraProps: TwinDictionary
    }[]
}


const InputTableSelectPaymentMethodCopyDates: React.FC<InputTableSelectPaymentMethodCopyDatesProps> = ({ completeData, className, tableConfiguration, columns, name, TableChildren, hookFunction, headerChanges, tableTitle, paymentMethods, paymentTypes, extraFields, ...rest }) => {
    const { setSelectedColumnsAndFilters, memorizedColumns, selectedColumnsAndFilters, } = useConfigColumns({ columns, name })
    const { parsedColumns, rowRenderer, containerRenderer, filters, ...resty } = hookFunction({ completeData, columns: memorizedColumns, ...rest })
    const parsedTableData = completeData?.filteredTableData || completeData.tableData
    const parseFilteredData = useCallback((rowIndex: number) => {
        return completeData?.tableData?.[completeData.dictPosTableData?.[parsedTableData?.[rowIndex].id]]
    }, [completeData.dictPosTableData, completeData.tableData, parsedTableData])
    return (
        <div className={'flex-col flex-auto flex input_table_contents ' + (className || '')} >
            <div className='flex flex-wrap gap-10 justify-between items-center'>
                <HeaderNewRemittance {...{ paymentMethods, paymentTypes, extraFields }}setCompleteData={rest.setCompleteData} />
                <ButtonConfigColumnsWithModal name={name} columns={columns} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} selectedColumnsAndFilters={selectedColumnsAndFilters} haveFiltersColumn={ false} setTableConfiguration={() => { }} getTableDataFromStart={() => { }} hideLoadFiltersButton={true} />
            </div>
            <div className='mt-30 flex flex-col flex-auto' >
                <div className={'flex flex-col flex-auto h-1 min-h-300 ' + (completeData?.tableData?.length !== parsedTableData?.length ? 'searching_input_table' : '')}>
                    <div className='flex-auto input_table_table'>
                        <TableWithCustomVirtualListing columns={parsedColumns} data={parsedTableData || []} setSelectedColumnsAndFilters={setSelectedColumnsAndFilters} name={name} updateEmployeeParameter={true} headerChanges={{ changeOrder: () => { }, changeSingleField: resty.eventFunctions.changeSingleField }} filters={filters} getDataFnc={completeData?.filteredTableData ? parseFilteredData : undefined} />
                    </div>
                </div>
            </div>
        </div>
    )
}


interface HeaderNewRemittanceProps extends HeaderNewRemittanceLogicProps {
    paymentTypes: PaymentTypesPayload
    paymentMethods: PaymentMethodsPayload
    extraFields?: {
        Component: React.FC<any>
        field: string
        label: string
        extraProps: TwinDictionary
    }[]
}

const HeaderNewRemittance: React.FC<HeaderNewRemittanceProps> = ({ setCompleteData, paymentTypes, paymentMethods, extraFields }) => {
    const { t } = useTwinTranslation()
    const { onChangeSearchBar, changeSingleField } = useHeaderNewRemittanceLogic({ setCompleteData })
    const memoPaymentTypes = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(paymentTypes?.all))), [paymentTypes])
    const memoPaymentMethods = useMemo(() => addDictionaryExtraLine(JSON.parse(JSON.stringify(paymentMethods?.active || {}))), [paymentMethods])
    const renderThis: JSX.Element[] = []
    if(extraFields){
        for (const element of extraFields) {
            const { Component , field, label, extraProps } = element
            renderThis.push(<Component key={field} label={label} onChange={(value: any) => changeSingleField(field, String(value))} {...extraProps} />)
        }
    }
    return (
        <div className='flex items-end gap-x-20 my-20'>
            <SearchBarInputTable onChange={onChangeSearchBar} />
            <InputCalendarStateFull label={t('from', 'Desde')} onChange={(value) => changeSingleField('from', value)} onlyValids={true} />
            <InputCalendarStateFull label={t('to', 'Hasta')} onChange={(value) => changeSingleField('to', value)} onlyValids={true} />
            <CustomSelectWithSearchBar label={t('paymentMethod', 'Método de pago')} onChange={(value) => changeSingleField('PaymentMethodId', value)} items={memoPaymentMethods} className='w-200' sortItems={true} />
            <CustomSelectWithSearchBar label={t('paymentType', 'Tipo de pago')} onChange={(value) => changeSingleField('PaymentTypeId', value)} items={memoPaymentTypes} className='w-200' sortItems={true} />
            {renderThis}
        </div>
    )
}

interface HeaderNewRemittanceLogicProps {
    setCompleteData: React.Dispatch<React.SetStateAction<InputTableLayoutData | null>>
}

export const useHeaderNewRemittanceLogic = ({ setCompleteData }: HeaderNewRemittanceLogicProps) => {

    const parseTableData = useCallback((createFilters: (oldFilters: RemittanceDataFilters) => RemittanceDataFilters, remittanceData?: InputTableLayoutData) => {
        setCompleteData((old) => {
            if (!old && !remittanceData) {
                return null
            }
            let myTableData = old
            const filters = myTableData?.filters ? createFilters(myTableData?.filters) : { generalSearchbar: '', order: {}, singleFieldsSearch: {} }
            if (remittanceData) {
                myTableData = remittanceData
            }
            if (!myTableData) {
                return null
            }
            let myfilteredTableData: any = []
            if (filters.generalSearchbar !== '' || Object.keys(filters.singleFieldsSearch)?.length) {
                myfilteredTableData = JSON.parse(JSON.stringify(filterRemittanceData(myTableData?.tableData || [], filters)))
                return { ...myTableData, filteredTableData: myfilteredTableData, filters } as any
            }

            return { ...myTableData, filteredTableData: null, filters }
        })
    }, [setCompleteData])

    const onChangeSearchBar = useCallback((value: string) => {
        parseTableData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            return { ...copy, generalSearchbar: value }
        })
    }, [parseTableData])

    const changeSingleField = useCallback((fieldName: string, value: string) => {
        parseTableData((old) => {
            const copy = JSON.parse(JSON.stringify(old))
            let myFilters = {
                ...copy,
                singleFieldsSearch: {
                    ...copy.singleFieldsSearch,
                    [fieldName]: value
                }
            }
            if (value === '') {
                delete myFilters.singleFieldsSearch[fieldName]
            }
            return myFilters
        })
    }, [parseTableData])
    return { onChangeSearchBar,  changeSingleField }
}
export default InputTableSelectPaymentMethodCopyDates